/*eslint class-methods-use-this: "off"*/
import geo, { GeoEvent } from 'geojs';
import {
  boundToGeojson,
  reOrdergeoJSON,
  getRotationFromAttributes,
  getRotationArrowLine,
  hasSignificantRotation,
  ROTATION_ATTRIBUTE_NAME,
  RectBounds,
  getRotationBetweenCoordinateArrays,
  rotateGeoJSONCoordinates,
  areRectangleSidesParallel,
} from '../utils';
import { FrameDataTrack } from './LayerTypes';
import BaseLayer, { BaseLayerParams, LayerStyle } from './BaseLayer';

export type EditAnnotationTypes = 'Point' | 'rectangle' | 'Polygon' | 'LineString';
interface EditAnnotationLayerParams {
  type: EditAnnotationTypes;
  /** Edits a detection's box corners alongside another edit layer. */
  companion?: boolean;
}

interface EditHandleStyle {
  type: string;
  x: number;
  y: number;
  index: number;
  editHandle: boolean;
}

const typeMapper = new Map([
  ['LineString', 'line'],
  ['Polygon', 'polygon'],
  ['Point', 'point'],
  ['rectangle', 'rectangle'],
]);
/**
 * correct matching of drag handle to cursor direction relies on strict ordering of
 * vertices within the GeoJSON coordinate list using utils.reOrdergeoJSON()
 * and utils.reOrderBounds()
 */
const rectVertex = [
  'sw-resize',
  'nw-resize',
  'ne-resize',
  'se-resize',
];
const rectEdge = [
  'w-resize',
  'n-resize',
  'e-resize',
  's-resize',
];
/**
 * This class is used to edit annotations within the viewer
 * It will do and display different things based on it either being in
 * rectangle or edit modes
 * Basic operation is that changedData will start the edited annotation
 * emits 'update:geojson' when data is changed
 */
export default class EditAnnotationLayer extends BaseLayer<GeoJSON.Feature> {
  skipNextExternalUpdate: boolean;

  _mode: 'editing' | 'creation';

  type: EditAnnotationTypes;

  styleType?: string;

  selectedKey?: string;

  selectedPolygonIndex: number;

  selectedHandleIndex: number;

  hoverHandleIndex: number;

  disableModeSync: boolean; //Disables fallthrough mouse clicks when ending an annotation

  leftButtonCheckTimeout: number; //Fallthough mouse down when ending lineStrings

  /* in-progress events only emitted for lines and polygons */
  shapeInProgress: GeoJSON.LineString | GeoJSON.Polygon | null;

  /* Track if the last click was a right-click or shift-click for Point mode */
  lastClickWasBackground: boolean;

  /** A middle press in Point mode, placed as a negative point only if released in place. */
  private pendingBackgroundPoint: { geo: { x: number; y: number } } | null = null;

  /* Track shift key state from native DOM events (more reliable than GeoJS events) */
  lastShiftKeyState: boolean;

  /* Bound event handlers for cleanup */
  private boundTrackShiftKey: ((e: MouseEvent) => void) | null = null;

  private boundHandleContextMenu: ((e: MouseEvent) => void) | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arrowFeatureLayer: any;

  unrotatedGeoJSONCoords: GeoJSON.Position[] | null;

  companion: boolean;

  /* The other edit layer live on this map, when a line and its box are edited together */
  peer: EditAnnotationLayer | null;

  /* GeoJS sends every edit drag to every annotation layer in edit mode */
  ownsDrag: boolean;

  constructor(params: BaseLayerParams & EditAnnotationLayerParams) {
    super(params);
    this.skipNextExternalUpdate = false;
    this._mode = 'editing';
    this.selectedKey = '';
    this.selectedPolygonIndex = 0;
    this.type = params.type;
    this.selectedHandleIndex = -1;
    this.hoverHandleIndex = -1;
    this.shapeInProgress = null;
    this.disableModeSync = false;
    this.leftButtonCheckTimeout = -1;
    this.lastClickWasBackground = false;
    this.lastShiftKeyState = false;
    this.unrotatedGeoJSONCoords = null;
    this.companion = !!params.companion;
    this.peer = null;
    this.ownsDrag = false;

    // Bind event handlers once (listeners are added/removed dynamically based on type)
    this.boundTrackShiftKey = this.trackShiftKey.bind(this);
    this.boundHandleContextMenu = this.handleContextMenu.bind(this);

    // Add listeners if starting in Point mode
    if (this.type === 'Point') {
      this.addPointModeListeners();
    }

    //Only initialize once, prevents recreating Layer each edit
    this.initialize();
  }

  /**
   * Add event listeners needed for Point mode (segmentation).
   */
  private addPointModeListeners() {
    if (this.boundTrackShiftKey) {
      document.addEventListener('mousedown', this.boundTrackShiftKey, true);
    }
    if (this.boundHandleContextMenu) {
      document.addEventListener('contextmenu', this.boundHandleContextMenu, true);
    }
  }

  /**
   * Remove event listeners used for Point mode.
   */
  private removePointModeListeners() {
    if (this.boundTrackShiftKey) {
      document.removeEventListener('mousedown', this.boundTrackShiftKey, true);
    }
    if (this.boundHandleContextMenu) {
      document.removeEventListener('contextmenu', this.boundHandleContextMenu, true);
    }
  }

  /**
   * Track shift key state from native DOM mousedown events.
   * This is more reliable than GeoJS events for detecting shift+click.
   */
  trackShiftKey(e: MouseEvent) {
    this.lastShiftKeyState = e.shiftKey;
    // Also track middle-click (button 1) from native events for background points.
    // Every Point-mode layer hears this document-level event, but only the one
    // under the cursor consumes it, so a left click must clear a flag left over
    // from a middle click that landed on another camera or off the canvas.
    if (this.type === 'Point' && this.getMode() === 'creation') {
      this.lastClickWasBackground = e.button === 1;
    }
  }

  /**
   * Handle right-click context menu in Point mode.
   * In segmentation mode, right-click confirms/locks the annotation.
   * Prevents browser context menu.
   */
  handleContextMenu(e: MouseEvent) {
    if (this.type === 'Point' && this.getMode() === 'creation') {
      e.preventDefault();
      e.stopPropagation();

      // Native contextmenu is used because GeoJS mouseclick may not fire for
      // right-button on Windows/Electron.
      const [mapNode] = this.annotator.geoViewerRef.value?.node?.() ?? [];
      if (mapNode && e.target instanceof Node && !mapNode.contains(e.target)) {
        // A right-click on another camera may move this edit there; deselecting
        // here first would flash the detection unselected.
        this.bus.$emit('confirm-annotation-elsewhere', e.buttons !== 0);
        return;
      }
      this.bus.$emit('confirm-annotation', e.buttons !== 0);
    }
  }

  /**
   * Clean up event listeners when the layer is destroyed.
   */
  destroy() {
    this.removePointModeListeners();
    this.boundTrackShiftKey = null;
    this.boundHandleContextMenu = null;
  }

  /**
   * Initialization of the layer should only be done once for edit layers
   * Handlers for edit_action and state which will emit data when necessary
   */
  initialize() {
    if (!this.featureLayer && this.type) {
      this.featureLayer = this.annotator.geoViewerRef.value.createLayer('annotation', {
        clickToEdit: true,
        showLabels: false,
        continuousPointProximity: false,
        finalPointProximity: 1,
        adjacentPointProximity: 1,
      });
      // Handle segment insertion before GeoJS's default click handler, which
      // otherwise exits edit mode when the click is not on a drag handle.
      const defaultClick = this.featureLayer._handleMouseClick;
      this.featureLayer.geoOff(geo.event.mouseclick, defaultClick);
      this.featureLayer.geoOn(geo.event.mouseclick, (e: GeoEvent) => {
        if (!this.insertLineVertex(e)) defaultClick(e);
      });
      // For these we need to use an anonymous function to prevent geoJS from erroring
      this.featureLayer.geoOn(
        geo.event.annotation.edit_action,
        (e: GeoEvent) => this.handleEditAction(e),
      );
      this.featureLayer.geoOn(
        geo.event.annotation.state,
        (e: GeoEvent) => this.handleEditStateChange(e),
      );
      //Event name is misleading, this means hovering over an edit handle
      this.featureLayer.geoOn(
        geo.event.annotation.select_edit_handle,
        (e: GeoEvent) => this.hoverEditHandle(e),
      );
      this.featureLayer.geoOn(geo.event.mouseclick, (e: GeoEvent) => {
        // The peer layer reports clicks that leave edit mode.
        if (this.companion) return;
        if (e.buttonsDown.middle) {
          this.handleMiddleClick(e);
          return;
        }
        if (this.type === 'LineString' && e.handled) return;
        // Right-click in creation mode (non-Point): cancel and fully deselect.
        // Point mode has its own right-click handler (handleContextMenu).
        if (e.buttonsDown.right && this.getMode() === 'creation' && this.type !== 'Point') {
          this.shapeInProgress = null;
          this.bus.$emit('editing-annotation-sync', false, true);
          return;
        }
        if (e.buttonsDown.right && this.type === 'Polygon' && this.featureLayer.annotations()[0]) {
          this.bus.$emit('polygon-edit-right-click', e.geo);
        }
        //Used to sync clicks that kick out of editing mode with application
        //This prevents that pseudo Edit state when left clicking on a object in edit mode
        if (!this.disableModeSync && (e.buttonsDown.left || e.buttonsDown.right)
          && this.getMode() === 'disabled' && this.featureLayer.annotations()[0]) {
          this.bus.$emit('editing-annotation-sync', false);
        } else if (e.buttonsDown.left) {
          const newIndex = this.hoverHandleIndex;
          // If not hovering over an edit handle and not on the edit polygon,
          // emit event so other layers can handle the click (e.g., selecting different polygon)
          if (newIndex < 0 && this.type === 'Polygon') {
            const annotations = this.featureLayer.annotations();
            if (annotations.length > 0) {
              const annotation = annotations[0];
              const geojson = annotation.geojson();
              if (geojson && geojson.geometry && geojson.geometry.type === 'Polygon') {
                const coords = geojson.geometry.coordinates[0] as [number, number][];
                const point: [number, number] = [e.geo.x, e.geo.y];
                // Ray casting algorithm to check if point is inside polygon
                let inside = false;
                for (let i = 0, j = coords.length - 1; i < coords.length; j = i, i += 1) {
                  const xi = coords[i][0];
                  const yi = coords[i][1];
                  const xj = coords[j][0];
                  const yj = coords[j][1];
                  const intersect = ((yi > point[1]) !== (yj > point[1]))
                    && (point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi);
                  if (intersect) inside = !inside;
                }
                if (!inside) {
                  // Click is outside the current edit polygon - emit passthrough event
                  this.bus.$emit('click-outside-edit', e.geo);
                  return;
                }
              }
            }
          }
          // Click features like a toggle: unselect if it's clicked twice.
          if (newIndex === this.selectedHandleIndex) {
            this.selectedHandleIndex = -1;
          } else {
            this.selectedHandleIndex = newIndex;
          }
          let divisor = 1;
          if (['Polygon', 'LineString'].includes(this.type) && this.selectedHandleIndex >= 0) {
            divisor = 2;
          }
          window.setTimeout(() => this.redraw(), 0); //Redraw timeout to update the selected handle
          if (this.type !== 'rectangle') {
            this.bus.$emit(
              'update:selectedIndex',
              this.selectedHandleIndex / divisor,
              this.type,
              this.selectedKey,
            );
          }
        }
        this.disableModeSync = false;
      });
      this.featureLayer.geoOn(geo.event.actiondown, (e: GeoEvent) => {
        this.ownsDrag = !!this.featureLayer.currentAnnotation?._editHandle?.handle?.selected;
        if (!this.companion) this.setShapeInProgress(e);
      });
      this.featureLayer.geoOn(geo.event.actionup, () => this.handleActionUp());

      const arrowLayer = this.annotator.geoViewerRef.value.createLayer('feature', { features: ['line'] });
      this.arrowFeatureLayer = arrowLayer.createFeature('line');
      this.arrowFeatureLayer.style({
        position: (p: [number, number]) => ({ x: p[0], y: p[1] }),
        stroke: true,
        fill: false,
        strokeColor: () => (this.styleType ? this.typeStyling.value.color(this.styleType) : this.stateStyling.selected.color),
        strokeWidth: () => (this.styleType ? this.typeStyling.value.strokeWidth(this.styleType) : this.stateStyling.selected.strokeWidth),
        strokeOpacity: () => (this.styleType ? this.typeStyling.value.opacity(this.styleType) : this.stateStyling.selected.opacity),
        strokeOffset: 0,
      });
    }
  }

  skipNextFunc() {
    return () => { this.skipNextExternalUpdate = true; };
  }

  /**
   * A quick middle click places the held negative point. GeoJS reports a
   * press released within its click tolerance as mouseclick, and it unbinds
   * its document mouseup handler while doing so, so actionup never follows.
   */
  handleMiddleClick(e: GeoEvent) {
    const pending = this.pendingBackgroundPoint;
    this.pendingBackgroundPoint = null;
    if (this.type !== 'Point' || this.getMode() !== 'creation') return;
    const geo = pending?.geo ?? e.geo;
    if (!geo) return;
    const pointGeojson: GeoJSON.Feature<GeoJSON.Point> = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [Math.round(geo.x), Math.round(geo.y)] },
      properties: { background: true },
    };
    this.bus.$emit('update:geojson', 'editing', true, pointGeojson, this.type, this.selectedKey, this.skipNextFunc());
  }

  /** Only a press that moved past the click tolerance reaches actionup: a pan. */
  handleActionUp() {
    this.pendingBackgroundPoint = null;
  }

  /**
   * Listen to mousedown events and build a replica of the in-progress annotation
   * shape that GeoJS is keeps internally.  Emit the shape as update:in-progress-geojson
   */
  setShapeInProgress(e: GeoEvent) {
    // Allow middle click movement when placing points (except in Point mode where it creates background points)
    if (e.mouse.buttons.middle && !e.propogated && this.type !== 'Point') {
      return;
    }
    // Right-click should never add vertices - cancel/confirm is handled by
    // mouseclick (line/polygon cancel) and contextmenu (Point confirm)
    if (e.mouse.buttons.right) {
      return;
    }

    // Track if this is a background point (shift+click or middle-click) for Point mode
    // Check both GeoJS event modifiers and our native DOM event tracking for reliability
    // Preserve the value if it was already set to true by trackShiftKey (native event)
    if (this.type === 'Point' && this.getMode() === 'creation') {
      this.lastClickWasBackground = this.lastClickWasBackground
        || e.mouse.buttons.middle
        || e.mouse.modifiers.shift
        || this.lastShiftKeyState;
    }

    // GeoJS doesn't create points on middle-click. The press may also be the
    // start of a middle-button pan, so the negative point waits for release.
    if (this.type === 'Point' && this.getMode() === 'creation' && e.mouse.buttons.middle) {
      this.pendingBackgroundPoint = { geo: { x: e.mouse.geo.x, y: e.mouse.geo.y } };
      this.lastClickWasBackground = false;
      return;
    }

    if (this.getMode() === 'creation' && ['LineString', 'Polygon'].includes(this.type)) {
      if (this.shapeInProgress === null) {
        // Initialize a new in-progress shape
        this.shapeInProgress = {
          type: this.type as ('Polygon' | 'LineString'),
          coordinates: this.type === 'Polygon' ? [[]] : [],
        };
      }
      // Update the coordinates of the existing shape
      const newPoint: GeoJSON.Position = [Math.round(e.mouse.geo.x), Math.round(e.mouse.geo.y)];
      if (this.type === 'Polygon') {
        const coords = this.shapeInProgress?.coordinates as GeoJSON.Position[][];
        // Magic 0: there can only be a single polygon in progress at a time
        coords[0].push(newPoint);
      } else {
        const coords = this.shapeInProgress?.coordinates as GeoJSON.Position[];
        coords.push(newPoint);
        // Auto-complete LineString after 2 points (simple line with 2 endpoints)
        if (coords.length >= 2) {
          const feature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: this.shapeInProgress!,
            properties: {},
          };
          this.shapeInProgress = null;
          this.disableModeSync = true;
          this.bus.$emit(
            'update:geojson',
            'editing',
            true, // geometryCompleteEvent - line is complete
            feature,
            this.type,
            this.selectedKey,
          );
          return;
        }
      }
      this.bus.$emit(
        'update:geojson',
        'in-progress',
        false, // Geometry isn't complete
        {
          type: 'Feature',
          geometry: this.shapeInProgress,
          properties: {},
        },
        this.type,
        this.selectedKey,
        this.skipNextFunc(),
      );
      // triggers a mouse up while editing to make it seem like a point was placed
      window.setTimeout(() => this.annotator.geoViewerRef.value.interactor().simulateEvent(
        'mouseup',
        { map: { x: e.mouse.geo.x, y: e.mouse.geo.y }, button: 'left' },
      ), 0);
    } else if (this.shapeInProgress) {
      this.shapeInProgress = null;
    }
  }

  /** Insert at the clicked position along an open line, in screen pixels. */
  insertLineVertex(e: GeoEvent): boolean {
    if (this.type !== 'LineString' || this.getMode() !== 'editing' || !e.buttonsDown.left) return false;
    const annotation = this.featureLayer.annotations()[0];
    if (!annotation || annotation.state() !== 'edit') return false;
    const map = this.annotator.geoViewerRef.value;
    const vertices: { x: number; y: number }[] = annotation.options('vertices');
    const displayed = map.gcsToDisplay(vertices, null);
    const click = map.gcsToDisplay(e.geo);
    // Existing vertex clicks select handles for movement/deletion.
    const tolerance = 8;
    if (displayed.some((p: { x: number; y: number }) => Math.hypot(p.x - click.x, p.y - click.y) <= tolerance)) return false;
    let segment = -1;
    let nearest = tolerance;
    displayed.slice(1).forEach((b: { x: number; y: number }, i: number) => {
      const a = displayed[i];
      const dx = b.x - a.x; const dy = b.y - a.y;
      const t = ((click.x - a.x) * dx + (click.y - a.y) * dy) / (dx * dx + dy * dy);
      if (t <= 0 || t >= 1) return;
      const distance = Math.hypot(click.x - a.x - t * dx, click.y - a.y - t * dy);
      if (distance <= nearest) { nearest = distance; segment = i; }
    });
    if (segment < 0) return false;
    const coordinates = vertices.map((p) => ({ ...p }));
    coordinates.splice(segment + 1, 0, map.displayToGcs(click, null));
    annotation.options('vertices', coordinates);
    this.selectedHandleIndex = (segment + 1) * 2;
    this.hoverHandleIndex = this.selectedHandleIndex;
    this.formattedData = [annotation.geojson()];
    this.bus.$emit('update:geojson', 'editing', false, this.formattedData[0], this.type, this.selectedKey, this.skipNextFunc());
    this.bus.$emit('update:selectedIndex', segment + 1, this.type, this.selectedKey);
    e.handled = true;
    this.redraw();
    return true;
  }

  hoverEditHandle(e: GeoEvent) {
    // The map rebroadcasts this to every layer; only our own handles count.
    if (e.annotation && e.annotation.layer() !== this.featureLayer) return;
    const divisor = 2; // Vertex/edge handles alternate for polygons and open lines.
    if (e.enable && e.handle.handle.type === 'vertex') {
      if (e.handle.handle.selected
        && (e.handle.handle.index * divisor) !== this.hoverHandleIndex) {
        this.hoverHandleIndex = e.handle.handle.index * divisor;
      }
      if (!e.handle.handle.selected) {
        this.hoverHandleIndex = -1;
      }
    } else if (e.enable && ['center', 'edge'].includes(e.handle.handle.type)) {
      this.hoverHandleIndex = -1;
    }
    if (e.enable) {
      if (this.type === 'rectangle') {
        if (e.handle.handle.type === 'vertex') {
          this.annotator.setCursor(rectVertex[e.handle.handle.index]);
        } else if (e.handle.handle.type === 'edge') {
          this.annotator.setCursor(rectEdge[e.handle.handle.index]);
        } else if (e.handle.handle.type === 'rotate') {
          this.annotator.setCursor('grab');
        }
      } else if (e.handle.handle.type === 'vertex') {
        this.annotator.setCursor('grab');
      } else if (e.handle.handle.type === 'edge') {
        this.annotator.setCursor('copy');
      }
      if (e.handle.handle.type === 'center') {
        this.annotator.setCursor('move');
      } else if (e.handle.handle.type === 'resize') {
        this.annotator.setCursor('nwse-resize');
      } else if (e.handle.handle.type === 'rotate') {
        this.annotator.setCursor('grab');
      }
    } else if (this.getMode() !== 'creation') {
      this.annotator.setCursor('default');
    }
  }

  applyStylesToAnnotations() {
    const annotation = this.featureLayer.annotations()[0];
    //Setup styling for rectangle and point editing
    if (annotation) {
      annotation.style(this.createStyle());
      annotation.editHandleStyle(this.editHandleStyle());
      annotation.highlightStyle(this.highlightStyle());
    }
    return annotation;
  }

  /**
   * Set the current Editing type for switching between editing polygons or rects.
   * Also manages event listeners that are only needed for Point mode (segmentation).
   */
  setType(type: EditAnnotationTypes) {
    const wasPoint = this.type === 'Point';
    const isPoint = type === 'Point';
    this.type = type;

    // Add or remove Point mode listeners based on type change
    if (!wasPoint && isPoint) {
      this.addPointModeListeners();
    } else if (wasPoint && !isPoint) {
      this.removePointModeListeners();
    }
  }

  setKey(key: string) {
    if (typeof key === 'string') {
      this.selectedKey = key;
    } else {
      throw new Error(`${key} is invalid`);
    }
  }

  /**
   * Provides whether the user is creating a new annotation or editing one
   */
  getMode(): 'creation' | 'editing' | 'disabled' {
    const layermode = this.featureLayer.mode();
    return layermode ? this._mode : 'disabled';
  }

  /**
   * Change the layer mode
   */
  setMode(
    mode: EditAnnotationTypes | null,
    geom?: GeoJSON.Feature,
  ) {
    if (mode !== null) {
      let newLayerMode: string;
      if (geom) {
        this._mode = 'editing';
        newLayerMode = 'edit';
        this.annotator.setCursor('default');
      } else if (typeMapper.has(mode)) {
        this._mode = 'creation';
        newLayerMode = typeMapper.get(mode) as string;
        this.annotator.setCursor('crosshair');
      } else {
        throw new Error(`No such mode ${mode}`);
      }
      this.featureLayer.mode(newLayerMode, geom);
      if (geom) this.guardPeerDrags(geom);
    } else {
      this.featureLayer.mode(null);
    }
  }

  calculateCursorImage() {
    const mode = this.getMode();
    if (mode === 'creation' || mode === 'editing') {
      this.annotator.setImageCursor(
        `mdi-vector-${typeMapper.get(this.type)}`,
        mode === 'editing',
      );
    } else {
      // Mode is disabled: drop any leftover creation/editing icon. Without
      // this, a cross-camera blank click can clear selection while a deferred
      // changeData still thinks the layer is editing and leaves the icon up.
      this.annotator.setImageCursor('');
    }
  }

  /**
   * Attempt to finalize any in-progress annotation before switching tracks.
   * Handles:
   * - Polygon with 3+ vertices tracked in shapeInProgress
   * - GeoJS-managed annotations (rectangles) in creation/done state
   * - Discards invalid partial shapes (polygon < 3 vertices, line with 1 point)
   * Returns true if a shape was finalized.
   */
  finalizeInProgress(): boolean {
    // Handle shapeInProgress (polygon/line tracked manually)
    if (this.shapeInProgress && this.getMode() === 'creation') {
      if (this.shapeInProgress.type === 'Polygon') {
        const coords = this.shapeInProgress.coordinates as GeoJSON.Position[][];
        if (coords[0] && coords[0].length >= 3) {
          const ring = coords[0];
          const first = ring[0];
          const last = ring[ring.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) {
            ring.push([...first]);
          }
          const feature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: this.shapeInProgress,
            properties: {},
          };
          this.disableModeSync = true;
          this.bus.$emit(
            'update:geojson',
            'editing',
            true,
            feature,
            this.type,
            this.selectedKey,
            this.skipNextFunc(),
          );
          this.shapeInProgress = null;
          return true;
        }
      }
      // Discard invalid partial shapes (polygon < 3 vertices, line with 1 point)
      this.shapeInProgress = null;
      return false;
    }

    // Handle GeoJS-managed annotations (rectangles, completed shapes)
    // Skip Point mode — segmentation manages its own polygon via the recipe,
    // not through the edit layer's GeoJS annotation.
    if (this.featureLayer && this.type !== 'Point') {
      // For LineString in creation mode, the native GeoJS annotation is a
      // garbage trace: the real feature is managed in shapeInProgress (handled
      // above). Emitting it would commit a bogus line to the track.
      if (this.type === 'LineString' && this.getMode() === 'creation') {
        return false;
      }
      const annotations = this.featureLayer.annotations();
      if (annotations.length > 0) {
        const annotation = annotations[0];
        const geoJSONData = annotation.geojson();
        if (geoJSONData && geoJSONData.geometry) {
          if (this.type === 'rectangle') {
            geoJSONData.geometry.coordinates[0] = reOrdergeoJSON(
              geoJSONData.geometry.coordinates[0] as GeoJSON.Position[],
            );
          }
          this.disableModeSync = true;
          this.bus.$emit(
            'update:geojson',
            'editing',
            true,
            geoJSONData,
            this.type,
            this.selectedKey,
            this.skipNextFunc(),
          );
          return true;
        }
      }
    }

    return false;
  }

  /**
   * With two edit layers on one map GeoJS applies a handle drag to both
   * annotations; only the one whose handle was grabbed may move.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  guardPeerDrags(annotation: any) {
    if (!annotation || annotation.diveDragGuard) return;
    const process = annotation.processEditAction;
    // eslint-disable-next-line no-param-reassign
    annotation.processEditAction = (evt: GeoEvent) => (
      this.peer && !this.ownsDrag ? undefined : process(evt));
    // eslint-disable-next-line no-param-reassign
    annotation.diveDragGuard = true;
  }

  /**
   * A mode change on the peer layer strips every annotation action from the
   * interactor, including the one for a handle still hovered on this layer
   * and the actions that place the next creation vertex.
   */
  restoreHandleActions() {
    const mode = this.getMode();
    if (mode === 'editing') {
      const handle = this.featureLayer.currentAnnotation?._editHandle?.handle;
      if (handle?.selected) {
        this.featureLayer._selectEditHandle({ data: handle }, true);
      }
    } else if (mode === 'creation') {
      // Re-enter creation so GeoJS reinstalls annotation actions. Peer
      // disable() calls mode(null), which clears the shared interactor
      // without leaving this layer's mode string.
      const layerMode = typeMapper.get(this.type);
      if (layerMode) this.featureLayer.mode(layerMode);
    }
  }

  /**
   * Removes the current annotation and resets the mode when completed editing
   */
  disable() {
    if (this.featureLayer) {
      // Cancel any changeData deferred while the left button was held (cross-
      // camera mousedown). LayerManager often calls disable() directly on
      // deselect; without this the timeout reloads the old edit geometry and
      // restores the editing cursor after the track is already cleared.
      clearTimeout(this.leftButtonCheckTimeout);
      this.leftButtonCheckTimeout = -1;
      this.skipNextExternalUpdate = false;
      // Skip redundant mode(null), which strips a peer's interactor actions,
      // but always clear overlays: GeoJS can finish editing before DIVE
      // disables the layer, leaving completed annotations in disabled mode.
      if (this.getMode() !== 'disabled') {
        this.setMode(null);
      }
      this.featureLayer.removeAllAnnotations(false);
      if (this.arrowFeatureLayer) {
        this.arrowFeatureLayer.data([]).draw();
      }
      this.shapeInProgress = null;
      this.pendingBackgroundPoint = null;
      if (this.selectedHandleIndex !== -1) {
        this.selectedHandleIndex = -1;
        this.hoverHandleIndex = -1;
        this.bus.$emit('update:selectedIndex', this.selectedHandleIndex, this.type, this.selectedKey);
      }
      if (!this.companion) {
        this.annotator.setCursor('default');
        this.annotator.setImageCursor('');
      }
    }
  }

  /**
   * retrieves geoJSON data based on the key and type
   * @param track
   * @param polygonIndex optional index to get a specific polygon when multiple exist
   */
  getGeoJSONData(
    track: FrameDataTrack,
    polygonIndex?: number,
  ): GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString | undefined {
    let geoJSONData: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString | undefined;
    if (track && track.features && track.features.geometry) {
      const matchingFeatures: (GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString)[] = [];
      track.features.geometry.features.forEach((feature) => {
        if (feature.geometry
            && feature.geometry.type.toLowerCase() === this.type.toLowerCase()) {
          // Get the feature key, defaulting to '' for undefined/null keys
          const featureKey = feature.properties?.key ?? '';
          if (featureKey === this.selectedKey) {
            matchingFeatures.push(
              feature.geometry as GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString,
            );
          }
        }
      });
      // If polygonIndex is specified and valid, use it; otherwise use first match
      if (polygonIndex !== undefined && polygonIndex >= 0 && polygonIndex < matchingFeatures.length) {
        geoJSONData = matchingFeatures[polygonIndex];
      } else if (matchingFeatures.length > 0) {
        [geoJSONData] = matchingFeatures;
      }
    }
    return geoJSONData;
  }

  /**
   * Set which polygon index to edit when multiple polygons exist
   */
  setPolygonIndex(index: number) {
    this.selectedPolygonIndex = index;
  }

  /**
   * Get the currently selected polygon index
   */
  getPolygonIndex() {
    return this.selectedPolygonIndex;
  }

  /** overrides default function to disable and clear anotations before drawing again */
  async changeData(frameData: FrameDataTrack[]) {
    if (this.skipNextExternalUpdate === false) {
      // disable() resets this layer before loading a new shape/mode, but a
      // cross-camera handoff triggers changeData during the very mousedown that
      // started an action on the newly selected camera, and resetting mid-action
      // forces the user to click a second time. Guard both cases:
      //  - Mid-creation stroke (polygon/line first vertex): skip the reset
      //    entirely, otherwise disable() clears shapeInProgress before the
      //    in-progress geometry is committed.
      //  - Left button still held (e.g. an edit handle just grabbed on the
      //    camera the mousedown selected): defer the reset until release, and
      //    crucially do NOT disable() first — that would cancel the drag.
      const midCreationStroke = this.shapeInProgress !== null
        && ['LineString', 'Polygon'].includes(this.type);
      if (!midCreationStroke) {
        // Always cancel a pending deferred reset before (re)deciding what to do.
        // Otherwise rapid changeData calls during a cross-camera edit stack up
        // multiple timeouts, and an earlier one — holding stale pre-edit
        // frameData in its closure — fires after mouse-up and snaps the
        // annotation back to its original geometry.
        clearTimeout(this.leftButtonCheckTimeout);
        //TODO: Find a better way to track mouse up after placing a point or completing geometry
        //For line drawings and the actions of any recipes we want
        if (this.annotator.geoViewerRef.value.interactor().mouse().buttons.left) {
          this.leftButtonCheckTimeout = window.setTimeout(() => this.changeData(frameData), 20);
        } else {
          // disable resets things before we load a new/different shape or mode
          this.disable();
          this.formattedData = this.formatData(frameData);
          this.rehoverEditHandles();
        }
      }
    } else {
      // A self-made change (e.g. a just-committed rectangle/geometry edit)
      // triggered this update. Skip it, and cancel any deferred reset still
      // pending from the drag so it can't overwrite the commit and revert the
      // annotation to its pre-edit geometry.
      clearTimeout(this.leftButtonCheckTimeout);
      this.skipNextExternalUpdate = false;
    }
    if (!this.companion) this.calculateCursorImage();
    this.redraw();
  }

  /**
   * GeoJS only fires mouseon when the handle under the cursor changes, so
   * handles rebuilt beneath a stationary cursor stay inert until the mouse
   * leaves and returns. Forget the stale hover and replay the mouse position.
   */
  rehoverEditHandles() {
    if (this.getMode() !== 'editing') return;
    window.setTimeout(() => {
      if (this.getMode() !== 'editing') return;
      this.featureLayer.features().forEach(
        (feature: { _clearSelectedFeatures?: () => void }) => feature._clearSelectedFeatures?.(),
      );
      this.annotator.geoViewerRef.value.interactor().retriggerMouseMove();
    }, 0);
  }

  /**
   *
   * @param frameData a single FrameDataTrack Array that is the editing item
   */
  formatData(frameData: FrameDataTrack[]) {
    this.selectedHandleIndex = -1;
    this.hoverHandleIndex = -1;
    this.bus.$emit('update:selectedIndex', this.selectedHandleIndex, this.type, this.selectedKey);
    if (frameData.length > 0) {
      const track = frameData[0];
      if (track.features && track.features.bounds) {
        let geoJSONData: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString | undefined;
        if (this.type === 'rectangle') {
          geoJSONData = boundToGeojson(track.features.bounds);
          this.unrotatedGeoJSONCoords = geoJSONData.coordinates[0] as GeoJSON.Position[];

          // Restore rotation if it exists
          const rotation = getRotationFromAttributes(track.features.attributes);
          if (hasSignificantRotation(rotation)) {
            // Apply rotation to restore the rotated rectangle for editing
            const updatedCoords = rotateGeoJSONCoordinates(geoJSONData.coordinates[0], rotation ?? 0);
            geoJSONData.coordinates[0] = updatedCoords;
          }
        } else {
          // TODO: this assumes only one polygon
          geoJSONData = this.getGeoJSONData(track);
        }
        if (!geoJSONData || this.type === 'Point') {
          this.setMode(this.type);
        } else {
          const geojsonFeature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: geoJSONData,
            properties: {
              annotationType: typeMapper.get(this.type),
              // Preserve rotation in properties
              ...(getRotationFromAttributes(track.features.attributes) !== undefined
                ? { [ROTATION_ATTRIBUTE_NAME]: getRotationFromAttributes(track.features.attributes) }
                : {}),
            },
          };

          if (track.styleType) {
            [this.styleType] = track.styleType;
          }

          this.featureLayer.geojson(geojsonFeature);
          const annotation = this.applyStylesToAnnotations();
          this.setMode(this.type, annotation);
          return [geojsonFeature];
        }
      }
    }
    // if there wasn't a valid track in frameData
    // then put the component into annotation create mode
    if (typeof this.type !== 'string') {
      throw new Error(
        `editing props needs to be a string of value 
          ${geo.listAnnotations().join(', ')}
            when geojson prop is not set`,
      );
    } else {
      // point or rectangle mode for the editor
      this.setMode(this.type);
    }
    return [];
  }

  /**
   * True when a finalized annotation has enough vertices to be a real shape.
   * Guards against degenerate geometry emitted by a programmatic mode change.
   */
  isValidCompletedGeometry(feature: GeoJSON.Feature): boolean {
    const { geometry } = feature;
    if (!geometry) return false;
    if (geometry.type === 'Polygon') {
      const ring = geometry.coordinates?.[0] as GeoJSON.Position[] | undefined;
      return !!ring && ring.length >= 3;
    }
    if (geometry.type === 'LineString') {
      const coords = geometry.coordinates as GeoJSON.Position[] | undefined;
      return !!coords && coords.length >= 2;
    }
    if (geometry.type === 'Point') {
      const coords = geometry.coordinates as GeoJSON.Position | undefined;
      return !!coords && coords.length >= 2;
    }
    return true;
  }

  /**
   *
   * @param e geo.event emitting by handlers
   */
  handleEditStateChange(e: GeoEvent) {
    if (this.featureLayer === e.annotation.layer()) {
      // Only calls this once on completion of an annotation
      if (e.annotation.state() === 'done' && this.getMode() === 'creation') {
        const geoJSONData = [e.annotation.geojson()];
        // Ignore degenerate geometry. A programmatic setMode (e.g. changeData([])
        // keeping the creation cursor live on a non-drawing camera) can finalize
        // an empty stale annotation as 'done'; committing it would add a bogus
        // shape. Real user completions always have enough vertices.
        if (!geoJSONData[0] || !this.isValidCompletedGeometry(geoJSONData[0])) {
          return;
        }
        if (this.type === 'rectangle') {
          geoJSONData[0].geometry.coordinates[0] = reOrdergeoJSON(
            geoJSONData[0].geometry.coordinates[0] as GeoJSON.Position[],
          );
        }
        // For Point mode, add background property if it was a right-click or shift-click
        if (this.type === 'Point' && this.lastClickWasBackground) {
          geoJSONData[0].properties = {
            ...geoJSONData[0].properties,
            background: true,
          };
          this.lastClickWasBackground = false; // Reset for next point
        }

        this.unrotatedGeoJSONCoords = this.type === 'rectangle'
          ? geoJSONData[0].geometry.coordinates[0] as GeoJSON.Position[] : null;
        this.formattedData = geoJSONData;
        // The new annotation is in a state without styling, so apply local stypes
        this.applyStylesToAnnotations();
        //This makes sure the click for the end point doesn't kick us out of the mode
        this.disableModeSync = true;

        this.bus.$emit(
          'update:geojson',
          'editing',
          this.getMode() === 'creation',
          this.formattedData[0],
          this.type,
          this.selectedKey,
          this.skipNextFunc(),
        );
      }
    }
  }

  /**
   * If we release the mouse after movement we want to signal for the annotation to update
   * @param e geo.event
   */
  handleEditAction(e: GeoEvent) {
    if (this.peer && !this.ownsDrag) return;
    if (this.featureLayer === e.annotation.layer()) {
      if (e.action === geo.event.actionup) {
        // This will commit the change to the current annotation on mouse up while editing
        if (e.annotation.state() === 'edit') {
          const newGeojson: GeoJSON.Feature<GeoJSON.Point|GeoJSON.Polygon|GeoJSON.LineString> = (
            e.annotation.geojson()
          );
          const newCoords = newGeojson.geometry.coordinates[0] as GeoJSON.Position[];
          // Rotation metadata belongs to rectangles. Point/line coordinates do
          // not contain polygon rings and must not enter the rotation helpers.
          let rotationBetween = 0;
          if (this.formattedData.length > 0 && this.type === 'rectangle') {
            const existingRotation = getRotationFromAttributes(this.formattedData[0].properties as Record<string, unknown>) ?? 0;
            const oldCoords = rotateGeoJSONCoordinates(
              this.unrotatedGeoJSONCoords || [],
              existingRotation,
            );
            if (areRectangleSidesParallel(oldCoords, newCoords)) {
              rotationBetween = existingRotation;
            } else {
              rotationBetween = getRotationBetweenCoordinateArrays(
                this.unrotatedGeoJSONCoords || [],
                newCoords,
              );
            }
          } else if (this.type === 'rectangle') {
            rotationBetween = getRotationBetweenCoordinateArrays(
              this.unrotatedGeoJSONCoords || [],
              newCoords,
            );
          }
          if (this.formattedData.length > 0) {
            if (this.type === 'rectangle') {
              // If rotated, keep the rotated coordinates for editing

              // The corners need to update for the indexes to update
              // Use the actual coordinates (rotated or not) to set corners correctly
              const currentCoords = newGeojson.geometry.coordinates[0] as GeoJSON.Position[];
              const cornerCoords = currentCoords.map(
                (coord: GeoJSON.Position) => ({ x: coord[0], y: coord[1] }),
              );
              // only use the 4 coords instead of 5
              const remapped = this.annotator.geoViewerRef.value.worldToGcs(cornerCoords.splice(0, 4));
              e.annotation.options('corners', remapped);
              //This will retrigger highlighting of the current handle after releasing the mouse
              setTimeout(() => this.annotator.geoViewerRef
                .value.interactor().retriggerMouseMove(), 0);

              // For rectangles, convert to axis-aligned bounds with rotation when saving
              // Convert to axis-aligned for storage, but keep rotation in properties
              const axisAlignedCoords = rotateGeoJSONCoordinates(newCoords, 0 - rotationBetween);
              newGeojson.properties = {
                ...newGeojson.properties,
                [ROTATION_ATTRIBUTE_NAME]: rotationBetween,
              };
              newGeojson.geometry.coordinates[0] = reOrdergeoJSON(axisAlignedCoords);
            }

            // update existing feature
            this.formattedData[0].geometry = newGeojson.geometry;
            if (newGeojson.properties) {
              this.formattedData[0].properties = {
                ...this.formattedData[0].properties,
                ...newGeojson.properties,
              };
            }
          } else {
            // create new feature
            // For rectangles, convert to axis-aligned bounds with rotation when saving
            if (this.type === 'rectangle') {
              const coords = newGeojson.geometry.coordinates[0] as GeoJSON.Position[];

              // Convert to axis-aligned for storage, but keep rotation in properties
              newGeojson.properties = {
                ...(newGeojson.properties || {}),
                [ROTATION_ATTRIBUTE_NAME]: rotationBetween,
              };
              newGeojson.geometry.coordinates[0] = rotateGeoJSONCoordinates(coords, 0 - (rotationBetween ?? 0));
            }

            this.formattedData = [{
              ...newGeojson,
              properties: {
                annotationType: this.type,
                ...(newGeojson.properties || {}),
              },
              type: 'Feature',
            }];
          }
          this.bus.$emit(
            'update:geojson',
            'editing',
            this.getMode() === 'creation',
            this.formattedData[0],
            this.type,
            this.selectedKey,
            this.skipNextFunc(),
          );
        }
      }
    }
  }

  /**
   * Drawing for annotations are handled during initialization they don't need the standard redraw
   * function from BaseLayer
   */
  redraw() {
    this.applyStylesToAnnotations();
    this.featureLayer.draw();

    if (this.arrowFeatureLayer) {
      if (this.type === 'rectangle') {
        const ann = this.featureLayer.annotations()[0];
        if (ann) {
          const g = ann.geojson();
          if (g && g.geometry && g.geometry.type === 'Polygon') {
            const coords = (g.geometry as GeoJSON.Polygon).coordinates[0];
            const rotation = getRotationFromAttributes(g.properties as Record<string, unknown>);
            const unrotated = rotateGeoJSONCoordinates(coords, 0 - (rotation ?? 0));
            // create RectBounds from unrotated coordinates
            const bounds: RectBounds = [Math.min(unrotated[0][0], unrotated[2][0]), Math.min(unrotated[0][1], unrotated[2][1]), Math.max(unrotated[0][0], unrotated[2][0]), Math.max(unrotated[0][1], unrotated[2][1])];
            const arrow = getRotationArrowLine(bounds, rotation ?? 0);
            if (arrow) {
              this.arrowFeatureLayer.data([{ c: arrow.coordinates }]).line((d: { c: GeoJSON.Position[] }) => d.c).draw();
              return;
            }
          }
        }
      }
    }
    this.arrowFeatureLayer.data([]).draw();
  }

  /**
   * The base style used to represent the annotation
   */
  createStyle(): LayerStyle<GeoJSON.Feature> {
    const baseStyle = super.createStyle();
    if (this.type === 'rectangle' || this.type === 'Polygon' || this.type === 'LineString') {
      return {
        ...baseStyle,
        fill: false,
        strokeColor: () => {
          if (this.styleType) {
            return this.typeStyling.value.color(this.styleType);
          }
          return this.stateStyling.selected.color;
        },

      };
    }
    return {
      fill: false,
      strokeWidth: 0,
      strokeColor: 'none',
    };
  }

  /**
   * Styling for the handles used to drag the annotation for ediing
   */
  editHandleStyle() {
    if (this.companion) {
      return {
        handles: {
          vertex: true, edge: false, center: false, rotate: false, resize: false,
        },
      };
    }
    if (this.type === 'rectangle') {
      return {
        handles: {
          rotate: true,
        },
      };
    }
    if (this.type === 'Point') {
      return {
        handles: false,
      };
    }
    if (this.type === 'Polygon' || this.type === 'LineString') {
      return {
        handles: {
          rotate: false,
          edge: true,
          // A two-point line's center handle obscures its insertion handle.
          center: this.type !== 'LineString',
        },
        fill: true,
        radius: (handle: EditHandleStyle): number => {
          if (handle.type === 'edge') {
            return 5;
          }
          return 8;
        },
        fillOpacity: (_: EditHandleStyle, index: number) => {
          if (index === this.selectedHandleIndex) {
            return 1;
          }
          return 0.25;
        },
        strokeColor: (_: EditHandleStyle, index: number) => {
          if (index === this.selectedHandleIndex) {
            return '#FF0000';
          }
          if (this.styleType) {
            return this.typeStyling.value.color(this.styleType);
          }
          return this.typeStyling.value.color('');
        },
        fillColor: (_data: EditHandleStyle, index: number) => {
          if (index === this.selectedHandleIndex) {
            return '#FF0000';
          }
          if (this.styleType) {
            return this.typeStyling.value.color(this.styleType);
          }
          return this.typeStyling.value.color('');
        },
      };
    }
    return {
      handles: {
        rotate: false,
      },
    };
  }

  /**
   * Should never have highlighting enabled but this will remove any highlighting style
   * from the annotation.  NOTE: this will not remove styling from handles
   */
  highlightStyle() {
    if (this.type === 'rectangle' || this.type === 'Polygon') {
      return {
        handles: {
          rotate: false,
        },
      };
    }
    if (this.type === 'Point') {
      return {
        stroke: false,
      };
    }
    return {
      handles: {
        rotate: false,
      },
    };
  }
}
